import { useCallback } from 'react';

import { publishBoardSocketPackage } from '@walls/services/boardWebsockets';
import { wallsApi } from '@walls/services/wallsApi';
import {
  useBoardSelectionStore,
  useWallContentStore,
  useWallStore,
  type CardId,
  type WallBoard,
  type WallCard,
} from '@walls/store';
import { deleteConnectionsByCardId } from '../../connections/connectionDeletion';

interface BoardPoint {
  x: number;
  y: number;
}

interface BoardSize {
  height: number;
  width: number;
}

export function useClusterCommands(board: WallBoard) {
  const addCard = useWallContentStore((state) => state.addCard);
  const deleteClusterAndPromoteChildren = useWallContentStore(
    (state) => state.deleteClusterAndPromoteChildren
  );
  const detachCardFromCluster = useWallContentStore((state) => state.detachCardFromCluster);
  const moveCardToCluster = useWallContentStore((state) => state.moveCardToCluster);
  const sortClusterChildren = useWallContentStore((state) => state.sortClusterChildren);
  const updateCard = useWallContentStore((state) => state.updateCard);
  const clearSelection = useBoardSelectionStore((state) => state.clearSelection);
  const setCurrentElement = useBoardSelectionStore((state) => state.setCurrentElement);
  const topicKey = useWallStore((state) => state.topicKey);

  return {
    createClusterAt: useCallback(
      async (point: BoardPoint, title: string, color: string, size?: BoardSize | null) => {
        const cluster = await wallsApi.insertCluster(
          board.id,
          title,
          color,
          {
            height: size?.height,
            xPos: Math.round(point.x),
            width: size?.width,
            yPos: Math.round(point.y),
          },
          topicKey
        );

        addCard(cluster);
        setCurrentElement({ id: cluster.id, type: 'cluster' });
        publishBoardSocketPackage({
          action: 'topicClusterAddedToBoard',
          topic: board.id,
          cluster: { ...cluster, boardId: board.id },
        });

        return cluster;
      },
      [addCard, board.id, setCurrentElement, topicKey]
    ),

    addCardToCluster: useCallback(
      async (card: WallCard, cluster: WallCard, zPos: number) => {
        if (card.id === cluster.id) {
          return;
        }

        moveCardToCluster(card.id, cluster.id, zPos);
        await deleteConnectionsByCardId(board.id, card.id, topicKey);
        await wallsApi.addCardToCluster(board.id, cluster.id, card.id, zPos, topicKey);
        publishBoardSocketPackage({
          action: 'addCardToCluster',
          topic: board.id,
          updateDetail: { cardId: card.id, clusterId: cluster.id, zPos },
        });
      },
      [board.id, moveCardToCluster, topicKey]
    ),

    addClusterToCluster: useCallback(
      async (sourceCluster: WallCard, targetCluster: WallCard, zPos: number) => {
        if (sourceCluster.id === targetCluster.id) {
          return;
        }

        moveCardToCluster(sourceCluster.id, targetCluster.id, zPos);
        await deleteConnectionsByCardId(board.id, sourceCluster.id, topicKey);
        setCurrentElement({ id: sourceCluster.id, type: 'cluster' });
        await wallsApi.addClusterToCluster(
          board.id,
          targetCluster.id,
          sourceCluster.id,
          targetCluster.collapsed,
          zPos,
          topicKey
        );
        publishBoardSocketPackage({
          action: 'addClusterToCluster',
          topic: board.id,
          updateDetail: {
            sourceClusterId: sourceCluster.id,
            targetClusterId: targetCluster.id,
            zPos,
          },
        });
      },
      [board.id, moveCardToCluster, setCurrentElement, topicKey]
    ),

    collapseCluster: useCallback(
      async (clusterId: CardId) => {
        updateCard(clusterId, { collapsed: true });
        await wallsApi.collapseCluster(board.id, clusterId, topicKey);
        publishBoardSocketPackage({
          action: 'collapseCluster',
          topic: board.id,
          cluster: { id: clusterId },
        });
      },
      [board.id, topicKey, updateCard]
    ),

    expandCluster: useCallback(
      async (clusterId: CardId) => {
        updateCard(clusterId, { collapsed: false });
        await wallsApi.expandCluster(board.id, clusterId, topicKey);
        publishBoardSocketPackage({
          action: 'expandCluster',
          topic: board.id,
          cluster: { id: clusterId },
        });
      },
      [board.id, topicKey, updateCard]
    ),

    detachCardFromCluster: useCallback(
      async (parentCluster: WallCard, card: WallCard, xPos: number, yPos: number) => {
        detachCardFromCluster(card.id, xPos, yPos);

        if (card.persistCluster || card.cards.length > 0) {
          await wallsApi.detachClusterFromCluster(
            board.id,
            parentCluster.id,
            card.id,
            { collapsed: card.collapsed, xPos, yPos },
            topicKey
          );
          publishBoardSocketPackage({
            action: 'removeClusterFromCluster',
            topic: board.id,
            updateDetail: { clusterId: card.id, collapsed: card.collapsed, xPos, yPos },
          });
          return;
        }

        await wallsApi.detachCardFromCluster(
          board.id,
          parentCluster.id,
          card.id,
          { xPos, yPos },
          topicKey
        );
        publishBoardSocketPackage({
          action: 'removeCardFromCluster',
          topic: board.id,
          updateDetail: { cardId: card.id, clusterId: parentCluster.id, xPos, yPos },
        });
      },
      [board.id, detachCardFromCluster, topicKey]
    ),

    deleteCluster: useCallback(
      async (clusterId: CardId) => {
        const { promotedCards } = deleteClusterAndPromoteChildren(clusterId);
        clearSelection();

        await wallsApi.unclusterCard(board.id, clusterId, topicKey);

        await Promise.all([
          ...promotedCards
            .filter((card) => !card.parentId)
            .map((card) =>
              card.persistCluster || card.cards.length > 0
                ? wallsApi.updateClusterPosition(
                    board.id,
                    card.id,
                    {
                      clusterId: card.id,
                      xPos: Math.round(card.xPos ?? 0),
                      yPos: Math.round(card.yPos ?? 0),
                    },
                    topicKey
                  )
                : wallsApi.updateCardPosition(
                    board.id,
                    card.id,
                    {
                      id: card.id,
                      xPos: Math.round(card.xPos ?? 0),
                      yPos: Math.round(card.yPos ?? 0),
                    },
                    topicKey
                  )
            ),
          promotedCards.length > 0
            ? wallsApi.updateCardZIndexes(
                board.id,
                promotedCards.map((card) => ({ cardId: card.id, zPos: card.zPos ?? 1 })),
                topicKey
              )
            : Promise.resolve(),
        ]);

        await wallsApi.deleteCluster(board.id, clusterId, topicKey);
        publishBoardSocketPackage({
          action: 'boardClusterDeleted',
          topic: board.id,
          cluster: { id: clusterId },
        });
      },
      [board.id, clearSelection, deleteClusterAndPromoteChildren, topicKey]
    ),

    lockCluster: useCallback(
      async (clusterId: CardId) => {
        updateCard(clusterId, { isLocked: true, persistCluster: true });
        await wallsApi.lockCluster(board.id, clusterId, topicKey);
        publishBoardSocketPackage({
          action: 'lockCluster',
          topic: board.id,
          cluster: { id: clusterId },
        });
      },
      [board.id, topicKey, updateCard]
    ),

    resetClusterSize: useCallback(
      async (clusterId: CardId) => {
        updateCard(clusterId, { height: undefined, width: undefined });
        await wallsApi.updateCardSize(
          board.id,
          clusterId,
          { height: null, id: clusterId, width: null },
          topicKey
        );
        publishBoardSocketPackage({
          action: 'undoClusterResize',
          topic: board.id,
          size: { id: clusterId },
        });
      },
      [board.id, topicKey, updateCard]
    ),

    unlockCluster: useCallback(
      async (clusterId: CardId) => {
        updateCard(clusterId, { isLocked: false });
        await wallsApi.unlockCluster(board.id, clusterId, topicKey);
        publishBoardSocketPackage({
          action: 'unlockCluster',
          topic: board.id,
          cluster: { id: clusterId },
        });
      },
      [board.id, topicKey, updateCard]
    ),

    updateCluster: useCallback(
      async (clusterId: CardId, title: string, content: string, color: string) => {
        await wallsApi.updateCluster(board.id, clusterId, title, content, color, topicKey);
        updateCard(clusterId, { color, content, title });
        publishBoardSocketPackage({
          action: 'topicClusterUpdated',
          topic: board.id,
          cluster: { boardId: board.id, color, content, id: clusterId, title },
        });
      },
      [board.id, topicKey, updateCard]
    ),

    sortClusterChildren: useCallback(
      async (clusterId: CardId, orderedIds: CardId[]) => {
        sortClusterChildren(clusterId, orderedIds);
        await wallsApi.sortCluster(
          board.id,
          clusterId,
          orderedIds.map((id) => ({ id })),
          topicKey
        );
        publishBoardSocketPackage({
          action: 'sortCluster',
          topic: board.id,
          sortOrder: {
            clusterId,
            cards: orderedIds.map((id) => ({ id })),
          },
        });
      },
      [board.id, sortClusterChildren, topicKey]
    ),

    toggleClusterCollapsed: useCallback(
      async (cluster: WallCard) => {
        if (cluster.collapsed) {
          updateCard(cluster.id, { collapsed: false });
          await wallsApi.expandCluster(board.id, cluster.id, topicKey);
          publishBoardSocketPackage({
            action: 'expandCluster',
            topic: board.id,
            cluster: { id: cluster.id },
          });
        } else {
          updateCard(cluster.id, { collapsed: true });
          await wallsApi.collapseCluster(board.id, cluster.id, topicKey);
          publishBoardSocketPackage({
            action: 'collapseCluster',
            topic: board.id,
            cluster: { id: cluster.id },
          });
        }
      },
      [board.id, topicKey, updateCard]
    ),
  };
}
